import React from 'react';
import { ComboBoxChangeEvent, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import {
  AuthService,
  Component,
  Configuration,
  COOKIE,
  CookieUtil,
  DynamicStyleManager,
  Position,
  withFormControl,
  PuzzleButton,
  PuzzleRadio,
  Container,
  PuzzleToolbar, FORM_CONTROL
} from "puzzle";
import i18n from '../../i18n.js';
import * as _ from 'lodash';
import PreferencesCard from "./PreferencesCard";
import { FilterCombo } from "puzzle/components";
import { config } from "./Preferences.config";
import { Notification, NotificationGroup } from '@progress/kendo-react-notification'
import { Fade } from '@progress/kendo-react-animation'
import { setDocumentTitle } from "../util/Util";
import { LANGUAGES, PREFERENCES_DATA, STATE, THEME, VALUE_LOCALE } from "./Types";
import { RadioButtonChangeEvent, Switch } from '@progress/kendo-react-inputs';
import { FieldWrap } from "../UI/FieldWrap/FieldWrap";
import { Field } from "../UI/Field/Field";
import EmailPreference from "./EmailPreference/EmailPreference";
import { Keys } from "@progress/kendo-react-common";

const { BASE_ROUTE_NAME } = Configuration.get();

type INITIALIZE_USER_DATA = {
  consentUrl: string,
  userProfile: {
    userInfo: {
      userType: string
    }
  }
};

type PROPS = {
  initializeUserData: INITIALIZE_USER_DATA
  form: FORM_CONTROL,
  title: string
};


export class Preferences extends Component<PROPS, STATE> {

  timer: any;
  isClientView: boolean = true;

  state: STATE = {
    savedDataFlag: false,
    isSsoUser: false,
    formData: {
      theme: '',
      city: '',
      country: ''
    },
    notifications: [],
    preventTabSwitchOnScroll: false,
    previousLanguage: '',
    previousTheme: '',
    themes: {},
    dueSoonDurations: [],
    sendEmailJustForMySubmissions: false,
    viewType: 'COMFORTABLE',
    mailNotificationsEnabled: false,
    allLanguages: []
  };

  languageComboRef: any;

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    const { title, initializeUserData } = this.props;
    setDocumentTitle(title);
    this.dispatch('loadPreferencesData', Position.end);
    const userType = initializeUserData.userProfile?.userInfo.userType;
    if (userType && userType === 'VENDOR') {
      this.isClientView = false;
    }
  };

  /**
   * Saves all changes to state
   */
  handleChange = (event: RadioButtonChangeEvent | ComboBoxChangeEvent | MultiSelectChangeEvent | any) => {
    const value = event.value;
    const name = event.nativeEvent.target.name || (event.nativeEvent.target.component && event.nativeEvent.target.component.name) || '';
    this.setState((oldState: STATE) => {
      return {
        ...oldState,
        savedDataFlag: false,
        formData: {
          ...oldState.formData,
          [name]: value
        },
        previousTheme: oldState.formData.theme
      }
    });
  };

  /**
   * Saves view changes to state
   */
  handleViewChange = (event: RadioButtonChangeEvent) => {
    const value = event.value;
    this.setState({
      viewType: value
    })
  }

  /**
   * Hide 'Successfully saved data' flag
   */
  hideSuccess = () => {
    this.setState({ savedDataFlag: false })
  };

  /**
   * Return boolean isClientView
   */
  isClientViewCheck = () => this.isClientView

  /**
   * Mapping due soon
   *
   * @param numberOfHours
   */
  mapDueSoonDuration = (numberOfHours: number): VALUE_LOCALE => {
    return {
      value: numberOfHours,
      locale: this.translate('hours', { numberOfHours })
    }
  };

  /**
   * Returns data for save
   */
  getDataForSave = () => {
    const { formData, previousTheme, previousLanguage, viewType } = this.state;
    const { theme } = formData;
    const { autoRefresh, keyboardShortcuts, selectedLanguage, dueSoon, drawerOption } = this.props.form.getData();
    let changeThemePath = '';
    let languageChanged = selectedLanguage !== previousLanguage;
    const themeChanged = previousTheme !== theme;
    let previousPath = null;
    if (themeChanged) {
      previousPath = window.location.pathname.replace(`${BASE_ROUTE_NAME}/`, '/');
      changeThemePath = `/theme-provider/${theme}`;
    }
    let preferencesFormData: any = {
      ...formData,
      autoRefresh,
      selectedLanguage,
      keyboardShortcuts,
      drawerOption,
      dueSoon,
      viewType
    };
    if (this.props.initializeUserData && _.isString(this.props.initializeUserData.consentUrl)) {
      // consentUrl is set and cookie feature is active
      if (!CookieUtil.hasFunctionalCookie()) {
        delete preferencesFormData.theme;
        delete preferencesFormData.selectedLanguage;
        if (languageChanged) {
          this.props.form.setData({ selectedLanguage: previousLanguage });
        }
      }
    }
    return {
      languageChanged,
      themeChanged,
      path: changeThemePath,
      preferencesFormData,
      selectedLanguage,
      previousPath,
      themeCookieName: COOKIE.THEME,
      localeCookieName: COOKIE.LANGUAGE_LOCALE,
      theme,
      viewType
    }
  };

  /**
   * Returns true if changes exists, false otherwise
   *
   * @param preferenceData
   */
  hasUnsavedChanges = (preferenceData: PREFERENCES_DATA) => {
    const { viewType } = this.state;
    const { autoRefresh, keyboardShortcuts, selectedLanguage, dueSoon, drawerOption } = this.props.form.getData();
    const themeChanged = preferenceData.theme.split('.')[0] !== this.state.formData.theme.split('.')[0];
    const hasChanges = themeChanged || (dueSoon !== preferenceData.dueSoon) || (autoRefresh !== preferenceData.autoRefresh) || (keyboardShortcuts !== preferenceData.keyboardShortcuts) || (selectedLanguage !== preferenceData.language.locale) || (drawerOption !== preferenceData.drawerOption) || (viewType !== preferenceData.viewType);
    return hasChanges;
  };

  /**
   * Handles click on change password link
   *
   * @param event
   */
  onPasswordChangeClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as Element).classList?.contains('tp-color-link')) {
      this.redirectToChangePassword();
    }
  };

  /**
   * Handles keydown on password link
   *
   * @param event
   */
  onPasswordChangeKeyPress = (event: React.KeyboardEvent) => {
    if (event.keyCode === Keys.enter) {
      this.redirectToChangePassword();
    }
  };

  /**
   * Saves all preferences data, switches locale language, changes theme
   */
  savePreferences = () => {
    DynamicStyleManager.emptyStyles();
    this.dispatch('saveAndUpdatePreference', '', this.getDataForSave());
  };

  /**
   * Returns notification container
   */
  renderNotifications = () => {
    let notifications = (<></>);
    if (this.state.notifications.length) {
      notifications = (<EmailPreference
        isClientView={this.isClientView}
        notifications={this.state.notifications}
        sendEmailJustForMySubmissions={this.state.sendEmailJustForMySubmissions}
        mailNotificationsEnabled={this.state.mailNotificationsEnabled} />)
    }
    return notifications;
  };

  /**
   * Updates previous language
   *
   * @param locale
   */
  updatePreviousLanguage = (locale: string) => {
    this.setState({
      previousLanguage: locale
    });
  };

  /**
   * Display 'Successfully saved data' flag, and set timeout to close it
   */
  showSuccess = () => { //TODO deprecated - remove in the next version
    clearTimeout(this.timer);
    this.setState({ savedDataFlag: true }, () => {
      (this.timer as any) = setTimeout(
        this.hideSuccess
        , 1000);
    });
  };

  /**
   * Initially sets user preferences data
   */
  setPreferencesData(languagesData: LANGUAGES, preferencesData: PREFERENCES_DATA, themes: Record<string, THEME>, isSsoUser: boolean) {
    const {
      autoRefresh,
      city,
      country,
      dueSoon,
      dueSoonDurations,
      language,
      keyboardShortcuts,
      notifications,
      sendEmailJustForMySubmissions,
      mailNotificationsEnabled,
      drawerOption,
      theme,
      viewType,

    } = preferencesData;
    const themeKeys = Object.keys(themes);
    const defaultThemeKey = themeKeys[0];
    const defaultTheme = _.get(themes, `${defaultThemeKey}.name`);
    const previousTheme = theme || CookieUtil.get('tp-theme') || defaultTheme;
    const languageLocale = language?.locale;
    const selectedLanguage = languageLocale ? languageLocale : i18n.language; //set current language if language in preferences are not saved
    if (!preferencesData.theme) {
      preferencesData.theme = defaultTheme.toString();
    }
    if (!preferencesData.language) {
      preferencesData.language = languagesData.languages[0];
    }
    this.setState((oldState): STATE => {
      return {
        ...oldState,
        isSsoUser,
        themes,
        sendEmailJustForMySubmissions,
        mailNotificationsEnabled,
        allLanguages: languagesData.languages,
        notifications,
        previousLanguage: selectedLanguage,
        previousTheme,
        viewType,
        dueSoonDurations: dueSoonDurations.map(this.mapDueSoonDuration),
        formData: {
          ...oldState.formData,
          city,
          country,
          drawerOption,
          theme: previousTheme
        }
      }
    });
    this.props.form.setData({ autoRefresh, dueSoon, selectedLanguage, keyboardShortcuts, drawerOption, viewType });
  };

  /**
   * Updates dueSoon property on initializeUserData.preferences on Dispatcher
   */
  updateDueSoonOnDispatcher = (initializeUserData: any) => {
    const { dueSoon } = this.props.form.getData();

    return {
      ...initializeUserData,
      preferences: {
        ...initializeUserData.preferences,
        dueSoon
      }
    };
  };

  /**
   * Redirect to change password URL
   */
  redirectToChangePassword = () => {
    window.location.href = AuthService.getChangePasswordUrl();
  };

  /**
   * Render themes in appearances section
   */
  renderThemes = () => {
    let themeWidgets = [];
    const { themes } = this.state;

    for (let name in themes) {
      if (themes.hasOwnProperty(name)) {
        themeWidgets.push(
          this.renderThemeWidget((themes as Record<string, THEME>)[name], name)
        );
      }
    }

    return themeWidgets;
  };

  /**
   * Renders one theme widget within Appearances section
   */
  renderThemeWidget = (theme: THEME, name: string) => {
    const { name: themeName } = theme;
    const themeIconClass = (name === 'primavera-default') ? 'tp-icon tp-light-mode' : 'tp-icon tp-dark-mode';

    return (
      <div className={'tp-theme-switcher-item'} key={themeName} data-checked={this.state.formData.theme === themeName}>
        <Container
          gap={2}
          className={'tp-theme-switcher-item-field'}>
          <PuzzleRadio
            checked={this.state.formData.theme.split(".")[0] === themeName.split(".")[0]}
            name='theme'
            label={this.translate(name)}
            value={themeName}
            onChange={this.handleChange}
            id={themeName}
          />
          <i aria-hidden={true} className={themeIconClass} />
        </Container>
      </div>
    )
  };

  renderBody() {
    const { allLanguages, dueSoonDurations, savedDataFlag } = this.state;
    const { getFields, handleChange } = this.props.form;
    const { autoRefresh, dueSoon, keyboardShortcuts, selectedLanguage, drawerOption } = getFields();
    const autoRefreshLabel = autoRefresh.value ? this.translate('SRL-switch-label-on') : this.translate('SRL-switch-label-off');
    const keyboardShortcutsLabel = keyboardShortcuts.value ? this.translate('SRL-switch-label-on') : this.translate('SRL-switch-label-off');
    const drawerOptionLabel = drawerOption.value ? this.translate('SRL-switch-label-on') : this.translate('SRL-switch-label-off');
    const viewTypeOptions = [
      { value: 'COMFORTABLE', label: this.translate('viewTypeComfortable'), icon: 'tp-icon tp-comfortable-view' },
      { value: 'COMPACT', label: this.translate('viewTypeCompact'), icon: 'tp-icon tp-compact' },
    ]
    return (
      <Container
        vBox
        className={'tp-page tp-preference-page'}
      >
        <Container
          alignItemsCenter
          className={'tp-page-header'}
          displayFlex
          htmlTag={'section'}
          tabIndex={0}
          justifyContentBetween
          wrap
        >
          <h2 className={'tp-page-title'}>{this.translate('preferences')}</h2>
          <PuzzleToolbar className={'tp-page-header-action-toolbar'}>
            {this.getConsentPageBtn()}
            <PuzzleButton
              onClick={this.savePreferences}
              type="submit"
              themeColor={'primary'}
              disabled={!this.props.form.isValid()}
            >
              {this.translate('saveChanges')}
            </PuzzleButton>
          </PuzzleToolbar>
        </Container>
        <Container
          responsive={{ sm: 'k-vbox' }}
          alignItemsStart
          className={'tp-page-preference-body'}
          displayFlex
          id={'tp-page-preference-body'}
          gap={4}
        >
          <Container
            className="tp-page-preference-left"
            vBox
            gap={4}
          >
            <Container
              className={'tp-section-wrap'}
              hBox
              alignItemsStart
              gap={4}
            >
              <PreferencesCard label={this.translate('account')} className={'account'}>
                {
                  this.state.isSsoUser ?
                    <div tabIndex={0} onClick={this.onPasswordChangeClick}
                         onKeyDown={this.onPasswordChangeKeyPress}>
                      {this.translate('accountTextContentSso', {
                        className: 'tp-pointable tp-color-link tp-text-underline'
                      })}
                    </div>
                    :
                    <span>
                    {this.translate('accountTextContent')}
                  </span>
                }
              </PreferencesCard>
              <PreferencesCard label={this.translate('language')} className={'language'}>
                <FieldWrap>
                  <Field mandatory>
                    <FilterCombo
                      data={allLanguages}
                      valueField='locale'
                      textField='value'
                      required={true}
                      label={this.translate('pdLang')}
                      name='selectedLanguage'
                      onChange={handleChange}
                      popupSettings={{ className: 'tp-single-select-menu' }}
                      suggest={true}
                      handleFormChange={this.props.form.handleChange}
                      clearButton={false}
                      ref={(el: any) => {
                        this.languageComboRef = el
                      }}
                      translateMethod={this.translate.bind(this)}
                      valueAsSimpleType={true}
                      {...selectedLanguage}
                    />
                  </Field>
                </FieldWrap>
              </PreferencesCard>
            </Container>
            <PreferencesCard
              label={this.translate('accessibility')}
              className={'accessibility'}
            >
              <Container
                className={'tp-accessibility-section-item'}
              >
                <label className='tp-text-medium'>
                  {this.translate('autoRefresh')}
                </label>
                <Container
                  alignItemsCenter
                  hBox
                  className={'tp-switcher-with-label'}
                  justifyContentStart
                >
                  <Switch
                    className={'k-switch-gray'}
                    name={'autoRefresh'}
                    onChange={handleChange}
                    checked={autoRefresh?.value || false}
                    {...autoRefresh}
                  />
                  <span aria-hidden={true} className={'tp-switch-label'}>{autoRefreshLabel}</span>
                </Container>
                <p>
                  {this.translate('autoRefreshInfo')}
                </p>
              </Container>
              <Container
                className={'tp-accessibility-section-item'}
              >
                <label className='tp-text-medium'>
                  {this.translate('keyboardShortcuts')}
                </label>
                <Container
                  alignItemsCenter
                  hBox
                  className={'tp-switcher-with-label'}
                  justifyContentStart
                >
                  <Switch
                    name={'keyboardShortcuts'}
                    onChange={handleChange}
                    checked={keyboardShortcuts?.value || false}
                    {...keyboardShortcuts}
                  />
                  <span aria-hidden={true} className={'tp-switch-label'}>{keyboardShortcutsLabel}</span>
                </Container>
                <p>{this.translate('keyboardShortcutsInfo')}</p>
              </Container>
              <Container
                className={'tp-accessibility-section-item'}
              >
                <label className='tp-text-medium'>
                  {this.translate('detach_drawer_label')}
                </label>
                <Container
                  alignItemsCenter
                  hBox
                  className={'tp-switcher-with-label'}
                  justifyContentStart
                >
                  <Switch
                    name={'drawerOption'}
                    onChange={handleChange}
                    checked={drawerOption?.value || false}
                    {...drawerOption}
                  />
                  <span aria-hidden={true} className={'tp-switch-label'}>{drawerOptionLabel}</span>

                </Container>
                <p>
                  {this.translate('detach_drawer_message')}
                </p>
              </Container>
            </PreferencesCard>
            <PreferencesCard label={this.translate('appearance')} className={'appearance'}>
              <fieldset className='tp-theme-switcher-wrap'>
                <legend aria-hidden="true" className="sr-only">Theme</legend>
                <span className={'tp-theme-label tp-text-medium'}>{this.translate('theme')}</span>
                {this.renderThemes()}
              </fieldset>
              <fieldset className={'tp-theme-switcher-wrap'}>
                <legend aria-hidden="true" className="sr-only">View</legend>
                <span className={'tp-theme-label tp-text-medium'}>{this.translate('viewType')}</span>
                {viewTypeOptions.map(option => (
                  <div className={'tp-theme-switcher-item'} key={option.value}
                       data-checked={this.state.viewType === option.value}>
                    <Container
                      gap={2}
                      className={'tp-theme-switcher-item-field'}>
                      <PuzzleRadio
                        id={option.value}
                        name='viewType'
                        label={option.label}
                        value={option.value}
                        checked={this.state.viewType === option.value}
                        onChange={this.handleViewChange}
                      />
                      <i aria-hidden={true} className={option.icon} />
                    </Container>
                  </div>
                ))}

              </fieldset>
            </PreferencesCard>
            <PreferencesCard label={this.translate('dueDateIndicator')} className={'pd-due-date-section'}>
              <Container
                alignItemsCenter
                className={'tp-due-soon-wrap'}
                hBox
                wrap
              >
                <span className={'tp-space-right-1'}> {this.translate('submissionAlertsAction')}</span>
                <FieldWrap className={'tp-space-left-half-1'}>
                  <Field mandatory>
                    <FilterCombo
                      className={'tp-due-soon-combo'}
                      data={dueSoonDurations}
                      valueField='value'
                      name='dueSoon'
                      required={true}
                      label={this.translate('dueSoon')}
                      onChange={handleChange}
                      handleFormChange={this.props.form.handleChange}
                      textField='locale'
                      clearButton={false}
                      suggest={true}
                      translateMethod={this.translate.bind(this)}
                      valueAsSimpleType={true}
                      {...dueSoon}
                    />
                  </Field>
                </FieldWrap>
              </Container>
            </PreferencesCard>
          </Container>
          <div className="k-vbox tp-page-preference-right">
            <PreferencesCard
              label={this.translate('emailPreference')}
              className={'email-preference'}
            >
              {this.renderNotifications()}
            </PreferencesCard>
          </div>
        </Container>
        <Fade enter={true} exit={true}>
          {savedDataFlag &&
            <div role={'alert'}>
              <NotificationGroup style={{ bottom: '64px', right: '10px' }}>
                <Notification closable type={{ style: 'success', icon: true }}>
                  <span>{this.translate('notificationText')}</span>
                </Notification>
              </NotificationGroup>
            </div>}
        </Fade>
      </Container>
    );
  }

  private getConsentPageBtn() {
    let consentPage: any = null;
    if (this.props.initializeUserData && _.isString(this.props.initializeUserData.consentUrl)) {
      consentPage = (
        <PuzzleButton
          onClick={() => this.dispatch('goToConsentPage', Position.end)}
          themeColor={'primary'}
        >
          {this.translate('goToConsentPage')}
        </PuzzleButton>
      );
    }
    return consentPage;
  }
}

export default withFormControl(config, true)(Preferences);
